import React, { useEffect, useState, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Card, CardContent, IconButton, Link } from '@mui/joy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { supabase } from '../supabaseClient';
import PageLayout from '../components/PageLayout';
import StyledTable from '../components/StyledTable';
import { AuthContext } from '../context/AuthContext.jsx';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const ClientDetails = () => {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [records, setRecords] = useState([]);
  const [expiringCount, setExpiringCount] = useState(0);
  const [gisProjects, setGisProjects] = useState([]);
  const { userMetadata } = useContext(AuthContext);

  useEffect(() => {
    const fetchClient = async () => {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('id', id)
        .single();
      if (error) console.error('Error loading client:', error);
      else setClient(data);
    };

    const fetchProjects = async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .eq('client_id', id);
      if (error) console.error('Error loading projects:', error);
      else setProjects(data);
    };

    const fetchGisProjects = async () => {
      const { data, error } = await supabase
        .from('gis_projects')
        .select('*')
        .eq('client_id', id);
      if (error) console.error('Error loading GIS projects:', error);
      else setGisProjects(data);
    };

    fetchClient();
    fetchProjects();
    fetchGisProjects();
  }, [id]);

  useEffect(() => {
    if (projects.length > 0) {
      const fetchProjectRecords = async () => {
        const { data, error } = await supabase
          .from('project_records')
          .select('*')
          .in('project_id', projects.map(p => p.id));
        if (error) {
          console.error('Error loading records:', error);
        } else {
          setRecords(data);
          const soon = data.filter(record =>
            record.expiration_date &&
            new Date(record.expiration_date) - new Date() > 0 &&
            new Date(record.expiration_date) - new Date() <= 30 * 24 * 60 * 60 * 1000
          );
          setExpiringCount(soon.length);
        }
      };
      fetchProjectRecords();
    }
  }, [projects]);

  if (!client) return <Typography level="h4">Loading client...</Typography>;

  const statusCounts = { 'In Progress': 0, 'In Review': 0, 'Active': 0 };
  records.forEach(record => {
    const { start_date, application_date, issue_date, expiration_date } = record;

    if (start_date && application_date && !issue_date && !expiration_date) {
      statusCounts['In Review']++;
    } else if (start_date && !application_date && !issue_date && !expiration_date) {
      statusCounts['In Progress']++;
    } else if (issue_date && expiration_date) {
      statusCounts['Active']++;
    } else if (expiration_date && !issue_date) {
      statusCounts['Active']++;
    }
  });

  const statusChartData = {
    labels: Object.keys(statusCounts),
    datasets: [
      {
        label: 'Permit Count',
        data: Object.values(statusCounts),
        backgroundColor: ['#00A884', '#00796B', '#B2DFDB'],
      },
    ],
  };

  return (
    <PageLayout title="Client Details">
      <Box sx={{ mt: 3, mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 'md' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Box>
            <Typography level="h3" mb={1}>{client.name}</Typography>
            <Typography level="body2" mb={2} sx={{ color: '#00A884' }}>
              Total Projects: {projects.length}
            </Typography>
          </Box>
          <Box sx={{ width: 250, height: 250 }}>
            <Pie
              data={statusChartData}
              options={{
                plugins: {
                  datalabels: {
                    color: '#fff',
                    font: { weight: 'bold' },
                    formatter: (value, context) => {
                      const label = context.chart.data.labels[context.dataIndex];
                      return `${label}: ${value}`;
                    },
                  },
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography level="h4" mb={0.5}>
          Associated Permits and Bonds
        </Typography>
        <Typography level="body2" sx={{ color: '#D32F2F' }}>
          Expiring Soon: {expiringCount}
        </Typography>
      </Box>

      {projects.map((project) => {
        const projectRecords = records.filter(r => r.project_id === project.id);
        if (projectRecords.length === 0) return null;

        return (
          <Box key={project.id} sx={{ mb: 3 }}>
            <Typography level="h5" sx={{ mb: 1, display: 'flex', alignItems: 'center', color: '#00A884' }}>
              {project.name}
              <Box sx={{ display: 'flex', height: 12, borderRadius: 4, overflow: 'hidden', ml: 2, minWidth: 60 }}>
                {projectRecords.map((rec, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      flex: 1,
                      backgroundColor: rec.expiration_date ? '#00A884' : '#CFD8DC',
                      borderRight: idx !== projectRecords.length - 1 ? '1px solid white' : 'none',
                    }}
                  />
                ))}
              </Box>
            </Typography>

            <StyledTable
              columns={[
                { key: 'name', label: 'Name' },
                { key: 'record_type', label: 'Type' },
                { key: 'agency', label: 'Agency' },
                { key: 'status', label: 'Status' },
                { key: 'application_date', label: 'Application Date' },
                { key: 'expiration_date', label: 'Expiration' },
                { key: 'details', label: 'Details' },
              ]}
              rows={projectRecords.map((record) => {
                const expiresSoon =
                  record.expiration_date &&
                  new Date(record.expiration_date) - new Date() > 0 &&
                  new Date(record.expiration_date) - new Date() <= 30 * 24 * 60 * 60 * 1000;

                return {
                  name: record.name,
                  record_type: record.record_type,
                  agency: record.agency || '—',
                  status: record.status || '—',
                  application_date: record.application_date || '—',
                  expiration_date: (
                    <span>
                      {record.expiration_date || '—'}
                    </span>
                  ),
                  details: (
                    <IconButton
                      component={Link}
                      to={`/permits/${record.id}`}
                      size="sm"
                      color="neutral"
                      variant="outlined"
                    >
                      <InfoOutlinedIcon />
                    </IconButton>
                  ),
                  _expiringSoon: expiresSoon,
                  _rowSx: expiresSoon ? { backgroundColor: '#ffe5e5' } : {},
                };
              })}
            />
          </Box>
        );
      })}

      {gisProjects.length > 0 && (
        <Box sx={{ mt: 5 }}>
          <Typography level="h4" sx={{ mb: 2 }}>GIS Projects</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {gisProjects.map((project) => (
              <Card
                key={project.id}
                variant="outlined"
                sx={{
                  width: 260,
                  backgroundColor: 'background.surface',
                  boxShadow: 'sm',
                }}
              >
                <CardContent>
                  <Typography level="title-md" sx={{ mb: 0.5, fontWeight: 'md' }}>
                    {project.name}
                  </Typography>
                  <Typography level="body-sm" sx={{ mb: 1 }} noWrap>
                    {project.description}
                  </Typography>
                  <Link href={`/gis-projects/${project.id}`} underline="always">
                    View Map
                  </Link>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      )}
    </PageLayout>
  );
};

export default ClientDetails;
